import { useMemo, useState } from 'react'
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts'

/**
 * DailyTrendChart — gráfico de série diária com métrica selecionável.
 *
 * Mesmo gráfico usado na página Meta Ads, extraído para ser reaproveitado
 * (ex.: Dashboard do Google Ads). Recebe `series` (lista de pontos por data) e
 * `metrics` (as métricas selecionáveis, cada uma com key/label/fmt/color).
 */
const nf = new Intl.NumberFormat('pt-BR')
const cf = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const money = (v) => cf.format(v || 0)
const num = (v) => nf.format(Math.round(v || 0))
const pct = (v) => `${(v || 0).toFixed(2)}%`

const fmtValue = (fmt, v) => (fmt === 'currency' ? money(v) : fmt === 'percent' ? pct(v) : num(v))
const fmtAxis = (fmt, v) =>
  fmt === 'currency' ? cf.format(v).replace('R$', '').trim()
    : fmt === 'percent' ? `${(v || 0).toFixed(1)}%`
    : nf.format(v)

export default function DailyTrendChart({ series = [], metrics, title = 'Desempenho diário' }) {
  const [metric, setMetric] = useState(metrics[0].key)
  const active = metrics.find((m) => m.key === metric) || metrics[0]

  const chartData = useMemo(
    () => (series || []).map((d) => ({
      date: (d.date || '').slice(5), // MM-DD
      value: d[metric] ?? 0,
    })),
    [series, metric]
  )

  return (
    <div className="section-card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div className="t-title" style={{ fontSize: 15 }}>
          {title} — {active.label}
        </div>
        <select
          className="input"
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
          style={{ width: 'auto', minWidth: 160, padding: '8px 12px', fontSize: 13 }}
        >
          {metrics.map((m) => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </select>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="dtchart" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={active.color} stopOpacity={0.28} />
              <stop offset="100%" stopColor={active.color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.4} />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} />
          <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={52}
            tickFormatter={(v) => fmtAxis(active.fmt, v)} />
          <Tooltip formatter={(v) => [fmtValue(active.fmt, v), active.label]}
            labelStyle={{ fontSize: 12 }} />
          <Area type="monotone" dataKey="value" stroke={active.color} strokeWidth={2}
            fill="url(#dtchart)" name={active.label} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
